import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';

/**
 * Sets the browser tab/history title as "PayHOA | <Page>".
 *
 * The page name is resolved dynamically — no per-route config required:
 *   1. If a route declares an explicit `title` (e.g. `title: 'Unit Detail'`),
 *      that wins. Use this to override the derived name for a specific page.
 *   2. Otherwise the name is derived from the URL — the last meaningful path
 *      segment, humanized. e.g.
 *        /app/chart-of-accounts            -> "PayHOA | Chart of Accounts"
 *        /app/reports/cash-flow-statement  -> "PayHOA | Cash Flow Statement"
 *        /app/units/1234                   -> "PayHOA | Units"  (id segment skipped)
 *   3. If nothing meaningful is found, falls back to the product title, so
 *      behaviour is never worse than before.
 */
@Injectable({ providedIn: 'root' })
export class PayhoaTitleStrategy extends TitleStrategy {
    private static readonly PREFIX = 'PayHOA';

    private static readonly FALLBACK = 'Simple HOA Management Software';

    /** Layout/context segments that aren't real "pages". */
    private static readonly IGNORED_SEGMENTS = new Set(['app', 'admin', 'pmc', 'events']);

    /** Short connector words kept lowercase in the middle of a title. */
    private static readonly MINOR_WORDS = new Set(['of', 'vs', 'by', 'and', 'to', 'the', 'for', 'in', 'on', 'a', 'an']);

    constructor(private readonly title: Title) {
        super();
    }

    override updateTitle(snapshot: RouterStateSnapshot): void {
        const page = this.buildTitle(snapshot) ?? this.deriveFromUrl(snapshot.url);

        this.title.setTitle(
            page
                ? `${PayhoaTitleStrategy.PREFIX} | ${page}`
                : `${PayhoaTitleStrategy.PREFIX} | ${PayhoaTitleStrategy.FALLBACK}`,
        );
    }

    /** Pull a human-readable page name from the last meaningful URL segment(s). */
    private deriveFromUrl(url: string): string | undefined {
        const segments = url
            .split(/[?#]/)[0] // drop query string / fragment
            .split('/')
            .map(segment => segment.split(';')[0]) // drop matrix params
            .filter(segment => segment.length > 0
                && !PayhoaTitleStrategy.IGNORED_SEGMENTS.has(segment)
                && !this.looksLikeId(segment));

        const lastSegment = segments[segments.length - 1];
        const parentSegment = segments[segments.length - 2];

        if (!lastSegment) {
            return undefined;
        }

        // When the last segment is just a qualifier (e.g. .../unit-balance/detail
        // or .../payments/by-unit), include the parent so the title stays meaningful.
        if (parentSegment && this.isQualifier(lastSegment)) {
            // Humanize the joined string (not each piece) so minor-word casing
            // spans the boundary: payments/by-unit -> "Payments by Unit".
            return this.humanize(`${parentSegment}-${lastSegment}`);
        }

        return this.humanize(lastSegment);
    }

    /**
     * Trailing segments that read poorly on their own, so the parent segment is
     * prepended for context (e.g. .../bank-reconciliation/report -> "Bank Reconciliation Report").
     * Covers generic verbs (edit/create/...) and common closing nouns/states seen in our routes.
     */
    private isQualifier(segment: string): boolean {
        return segment.startsWith('by-')
            || [
                'detail', 'details', 'summary', 'list', 'new', 'edit', 'create', 'overview', 'archived',
                'dashboard', 'enroll', 'enrollment', 'scheduled', 'report', 'confirmation',
                'reschedule', 'cancel', 'homeowner', 'drafts', 'bookings',
            ].includes(segment);
    }

    /** Numeric ids and UUIDs aren't page names. */
    private looksLikeId(segment: string): boolean {
        return /^\d+$/.test(segment)
            || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
    }

    /** "cash-flow-statement" -> "Cash Flow Statement", "profit-vs-loss" -> "Profit vs Loss". */
    private humanize(segment: string): string {
        return segment
            .split('-')
            .map((word, index) => {
                if (index > 0 && PayhoaTitleStrategy.MINOR_WORDS.has(word)) {
                    return word;
                }

                return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(' ');
    }
}
